import { Component, OnInit, ChangeDetectionStrategy, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';
import {
  UserProgressService,
  SplashScreenService,
  SweepstakeService,
  FullscreenService,
  BreakPointsService,
} from '@shared/services';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { timer } from 'rxjs';

const consentPropertyName = 'CONSENT_CACHE';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[@.disabled]': 'prefersReducedMotion' },
})
export class AppComponent implements OnInit {
  isShowCookieConsent: boolean = false;

  get prefersReducedMotion(): boolean {
    return this.breakPointsService.prefersReducedMotion;
  }

  constructor(
    private platform: Platform,
    private breakPointsService: BreakPointsService,
    private route: ActivatedRoute,
    private metaService: Meta,
    @Inject(APP_BASE_HREF) private baseHref: string,
    // Do not remove the next lines. These lines initialize services for the app.
    private translate: TranslateService,
    private appSplashScreen: SplashScreenService,
    private sweepstakeService: SweepstakeService,
    private fullscreenService: FullscreenService,
    private userProgressService: UserProgressService,
  ) {
    translate.onLangChange.subscribe(({ lang }) => {
      document.documentElement.lang = lang;
    });
    translate.setDefaultLang('en');
    translate.use('en');
    const location = window.location;
    this.metaService.addTag({ name: 'og:url', content: location.protocol + '//' + location.host });
  }

  ngOnInit(): void {
    this.reloadWhenAnotherCompanyLoaded();

    this.platform.ready().then(() => {
      if (!localStorage.getItem(consentPropertyName)) {
        this.isShowCookieConsent = true;
      }
    });

    this.route.queryParams.pipe(takeUntil(timer(15000))).subscribe({
      next: (params) => {
        if (params['ltik']) {
          console.log('LTI launch detected, saving ltik to localStorage', params['ltik']);
          localStorage.setItem('ltik', params['ltik']);
        }
      },
      error: (err) => {
        console.error('Error retrieving query params:', err.message);
      },
    });
  }

  // One session per browser: a tab of another company loaded later owns the token and the companyCode cookie
  private reloadWhenAnotherCompanyLoaded(): void {
    const companyCode = this.baseHref.match(/^\/companies\/([^/]+)/)?.[1];
    if (!companyCode) return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const cookie = document.cookie.split('; ').find((c) => c.startsWith('companyCode='));
      const sessionCompanyCode = cookie && decodeURIComponent(cookie.slice('companyCode='.length));
      if (sessionCompanyCode !== companyCode) window.location.reload();
    });
  }

  onAcceptConsent() {
    localStorage.setItem(consentPropertyName, 'OK');
    this.isShowCookieConsent = false;
  }
}
